const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5215';

const makeRequest = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  const data = await response.json();
  if (!response.ok) {
    const err = new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
    err.status = response.status;
    err.details = data;
    throw err;
  }
  return data;
};

/**
 * Get all pipeline groups (build/release pairs with tenant lists).
 */
export const getPipelines = async () => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/pipelines`);
};

/**
 * Get branches for a service's repository.
 * @param {string} slug - Pipeline/service slug
 */
export const getBranches = async (slug) => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/branches?slug=${encodeURIComponent(slug)}`);
};

/**
 * Trigger build pipeline(s).
 * @param {Object} request - { serviceSlugs: string[], branch: string }
 */
export const triggerBuild = async (request) => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/trigger-build`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
};

/**
 * Get build run status.
 * @param {number} pipelineId - Build pipeline ID
 * @param {number} runId - Run ID
 */
export const getBuildStatus = async (pipelineId, runId) => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/builds/${pipelineId}/${runId}`);
};

/**
 * Trigger release pipeline(s) to tenants.
 * @param {Object} request - { serviceSlugs: string[], tenants: string[], deploymentMode?: string, buildRunId?: number }
 */
export const triggerRelease = async (request) => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/trigger-release`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
};

/**
 * Get recent runs for a specific pipeline.
 */
export const getPipelineRuns = async (pipelineId, top = 10) => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/pipelines/${pipelineId}/runs?top=${top}`);
};

/**
 * Get detailed info for a specific run (including timeline).
 */
export const getRunDetail = async (runId) => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/runs/${runId}`);
};

/**
 * Get the timeline (stages/jobs) for a specific run.
 */
export const getRunTimeline = async (runId) => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/runs/${runId}/timeline`);
};

/**
 * Trigger a release pipeline with selected tenants.
 * @param {Object} request - { pipelineId, branch, tenants[], deploymentMode, environment }
 */
export const triggerDeployment = async (request) => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/trigger`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
};

/**
 * Get the deployment status matrix across all pipelines.
 */
export const getStatusMatrix = async () => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/status`);
};

/**
 * Get tenants for BackOffice full deploy (nonProd and prod lists).
 */
export const getTenants = async () => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/tenants`);
};

/**
 * Get tenants for Simulcast full deploy (nonProd and prod lists).
 */
export const getSimulcastTenants = async () => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/simulcast/tenants`);
};

/**
 * Get branches for full deploy (from Blackbird_v2_API repo).
 */
export const getDeployBranches = async () => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/deploy-branches`);
};

/**
 * Start full deploy orchestration.
 * @param {string} product - 'backoffice' or 'simulcast'
 * @param {string[]} tenants - Tenant names (e.g. ['dev', 'qa'])
 * @param {string} branch - Branch name (e.g. development, main)
 * @returns {Promise<{ sessionId: string }>}
 */
export const startFullDeploy = async (product, tenants, branch, deploymentMode = 'staging') => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/full-deploy`, {
    method: 'POST',
    body: JSON.stringify({ product, tenants, branch, deploymentMode }),
  });
};

/**
 * Get full deploy session status (for polling).
 * @param {string} sessionId - Session ID from startFullDeploy
 */
export const getDeploySession = async (sessionId) => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/full-deploy/${encodeURIComponent(sessionId)}`);
};

/**
 * Get deployment history (most recent first).
 * @param {number} [count=50] - Max number of records to return
 */
export const getDeployHistory = async (count = 50) => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/history?count=${count}`);
};

/**
 * Get pending pipeline environment approvals.
 */
export const getPendingApprovals = async () => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/approvals`);
};

/**
 * Approve a pending deployment approval.
 * @param {string} approvalId - GUID of the approval
 * @param {string} [comment] - Optional comment
 */
export const approveDeployment = async (approvalId, comment) => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/approvals/${approvalId}/approve`, {
    method: 'POST',
    body: JSON.stringify({ comment: comment || '' }),
  });
};

/**
 * Reject a pending deployment approval.
 * @param {string} approvalId - GUID of the approval
 * @param {string} [comment] - Optional comment
 */
export const rejectDeployment = async (approvalId, comment) => {
  return await makeRequest(`${API_BASE_URL}/api/deployments/approvals/${approvalId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ comment: comment || '' }),
  });
};
